<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    default: () => [],
  },
  checklistItems: {
    type: Array,
    default: () => [],
  },
  attachments: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

// Estado para controlar expansão/colapso
const statisticsCollapsed = ref(false);

// Função para alternar estado
const toggleStatistics = () => {
  statisticsCollapsed.value = !statisticsCollapsed.value;
};

const totalChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.length;
});

const completedChecklistItems = computed(() => {
  if (!Array.isArray(props.checklistItems)) return 0;
  return props.checklistItems.filter(item => item.completed).length;
});

const calculateStageChanges = (activities) => {
  if (!activities || !Array.isArray(activities)) return 0;
  return activities.filter(activity => activity.type === 'stage_changed').length;
};

const stageChangesCount = computed(() => {
  return calculateStageChanges(props.item.item_details?.activities);
});

const itemStatistics = computed(() => {
  const timeInStage = props.item.stage_entered_at
    ? formatDistanceToNow(new Date(props.item.stage_entered_at), { locale: ptBR, addSuffix: false })
    : t('KANBAN.STATISTICS.NOT_AVAILABLE');

  const noteCount = props.notes?.length || 0;
  const checklistProgressText = `${completedChecklistItems.value}/${totalChecklistItems.value}`;
  const attachmentCount = props.attachments?.length || 0;

  return [
    { icon: 'lock', label: t('KANBAN.STATISTICS.TIME_IN_STAGE'), value: timeInStage },
    { icon: 'comment-add', label: t('KANBAN.STATISTICS.NOTES'), value: noteCount },
    { icon: 'checkmark-circle', label: t('KANBAN.STATISTICS.CHECKLIST'), value: checklistProgressText },
    { icon: 'attach', label: t('KANBAN.STATISTICS.ATTACHMENTS'), value: attachmentCount },
    { icon: 'arrow-right', label: t('KANBAN.STATISTICS.STAGE_CHANGES'), value: stageChangesCount.value },
  ];
});
</script>

<template>
  <div class="item-statistics bg-white dark:bg-slate-800 rounded-lg">
    <!-- Cabeçalho com botão de colapso -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-base font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chart-no-axes-column-icon lucide-chart-no-axes-column">
          <path d="M5 21v-6"/>
          <path d="M12 21V3"/>
          <path d="M19 21V9"/>
        </svg>
        <span>{{ t('KANBAN.STATISTICS.TITLE') }}</span>
      </div>
      <button
        type="button"
        @click="toggleStatistics"
        class="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        <fluent-icon
          :icon="statisticsCollapsed ? 'chevron-down' : 'chevron-up'"
          size="16"
        />
      </button>
    </div>

    <!-- Conteúdo colapsável -->
    <div v-show="!statisticsCollapsed" class="statistics-content transition-all duration-300 ease-in-out">
      <div class="flex flex-col sm:flex-row gap-2">
        <div
          v-for="stat in itemStatistics"
          :key="stat.label"
          class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-md w-full sm:flex-1"
        >
          <!-- Lado esquerdo: ícone + título -->
          <div class="flex items-center gap-1.5">
            <!-- Ícone inline para tempo na etapa -->
            <div v-if="stat.icon === 'lock'" class="flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock-icon lucide-clock text-slate-500 dark:text-slate-400">
                <path d="M12 6v6l4 2"/>
                <circle cx="12" cy="12" r="10"/>
              </svg>
            </div>
            <!-- Outros ícones continuam usando fluent-icon -->
            <fluent-icon
              v-else
              :icon="stat.icon"
              size="14"
              class="text-slate-500 dark:text-slate-400"
            />
            <span class="text-xs text-slate-500 dark:text-slate-400">
              {{ stat.label }}
            </span>
          </div>

          <!-- Lado direito: valor -->
          <span class="text-sm font-medium text-slate-800 dark:text-slate-200">
            {{ stat.value }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Adicione estilos específicos se necessário, mas as classes Tailwind devem funcionar */
.item-statistics {
  /* Estilos já aplicados via classes Tailwind */
}

/* Estilos para animação de colapso */
.statistics-content {
  overflow: hidden;
  transition: all 0.3s ease-in-out;
}

.statistics-content.collapsed {
  max-height: 0;
  opacity: 0;
  transform: translateY(-10px);
}

.statistics-content.expanded {
  max-height: 500px;
  opacity: 1;
  transform: translateY(0);
}
</style> 